// Small, dependency-free helpers for the PDF export feature. Extracted so they
// can be unit-tested in Node. The map-readiness helpers are shared with the map
// controllers; the heavy html2canvas/pdf-lib dependencies remain exclusive to
// the on-demand app/javascript/pdf_export.js bundle.

export const PDF_EXPORT_MAP_SELECTOR = "[data-pdf-export-map]"

// Conservative canvas limits. Desktop browsers allow large canvases, but mobile
// Safari caps the total pixel area (~16.7M px) and older devices cap each side
// near 4096px. We clamp html2canvas's scale so a very long drawer can never
// allocate a canvas beyond these limits, which would otherwise freeze or fail
// the capture before pagination could help.
export const MAX_CANVAS_DIMENSION = 8192
export const MAX_CANVAS_AREA = 16_777_216 // ~4096 * 4096 device px (mobile Safari)

// Given the CSS size of the element to capture and a desired device scale,
// return the largest scale (never above desired) that keeps the resulting
// canvas within both the per-side and total-area limits.
export function computeCaptureScale(
  cssWidth,
  cssHeight,
  desiredScale,
  { maxDimension = MAX_CANVAS_DIMENSION, maxArea = MAX_CANVAS_AREA } = {}
) {
  if (!(cssWidth > 0) || !(cssHeight > 0) || !(desiredScale > 0)) return desiredScale

  let scale = Math.min(desiredScale, maxDimension / cssWidth, maxDimension / cssHeight)
  if (cssWidth * cssHeight * scale * scale > maxArea) {
    scale = Math.sqrt(maxArea / (cssWidth * cssHeight))
  }
  // No artificial lower floor: for extremely large content the caps legitimately
  // drive the scale well below 1, and clamping it up would re-breach the limits.
  return Math.min(scale, desiredScale)
}

// The standard PDF fonts only encode WinAnsi. Keep printable ASCII (0x20-0x7E)
// and the Latin-1 supplement (0xA0-0xFF); drop the 0x80-0x9F control block (much
// of which is undefined in WinAnsi and would throw in drawText) and anything
// non-Latin, so a heading can't crash PDF generation.
export function sanitizeHeaderText(text) {
  return (text || "").replace(/[^\x20-\x7E\xA0-\xFF]/g, "").trim()
}

// Truncate with an ellipsis so a long heading stays within maxWidth. `measure`
// is a (text, size) => width function (e.g. pdf-lib's font.widthOfTextAtSize).
export function fitText(text, measure, size, maxWidth) {
  if (measure(text, size) <= maxWidth) return text
  let truncated = text
  while (truncated.length > 1 && measure(`${truncated}...`, size) > maxWidth) {
    truncated = truncated.slice(0, -1)
  }
  return `${truncated.trimEnd()}...`
}

// Only genuine scroll containers should have their height/overflow constraints
// released for capture. overflow: hidden is also used for visual clipping and
// intentionally-collapsed content, so it must NOT be treated as expandable.
export function isExpandableOverflow(overflowY) {
  return overflowY === "auto" || overflowY === "scroll"
}

// Resolve the element captured by a download control. Explicit selectors are
// useful outside drawers; the normal provider-search path captures only the
// closest drawer layout, which matters when provider-details drawers are stacked.
export function resolveCaptureTarget(element, captureSelector, documentRoot) {
  if (captureSelector) return documentRoot.querySelector(captureSelector)
  return element.closest("[data-drawer-layout]") || element
}

// Register a controller-owned readiness callback on its DOM element so the
// separately-loaded export bundle can discover it without importing Stimulus or
// Mapbox. Returns cleanup for Stimulus disconnect/reconnect cycles.
export function exposePdfExportMapReadiness(element, readiness) {
  element.setAttribute("data-pdf-export-map", "")
  element.pdfExportReady = readiness

  return () => {
    element.removeAttribute("data-pdf-export-map")
    delete element.pdfExportReady
  }
}

// Called after the capture target's height constraints have been released.
// Waiting for construction covers a click during the controller's initial RAF;
// resize makes Mapbox redraw for the expanded layout; idle covers style, tiles,
// and any fitBounds/flyTo transition started by the controller's load handler.
export async function prepareMapForPdfExport(mapInitialization) {
  const map = await mapInitialization
  if (!map) return

  map.resize()
  if (map.loaded() && map.areTilesLoaded() && !map.isMoving()) return

  await new Promise((resolve) => map.once("idle", resolve))
}

// Wait only for map controllers contained by this capture target. A failed map
// must not fail the whole PDF, and slow/broken Mapbox requests must not leave the
// download control busy forever, so all outcomes are bounded by one timeout.
export async function waitForPdfExportMaps(target, timeoutMs = 5000) {
  const mapElements = []
  if (target.matches?.(PDF_EXPORT_MAP_SELECTOR)) mapElements.push(target)
  target.querySelectorAll(PDF_EXPORT_MAP_SELECTOR).forEach((element) => mapElements.push(element))

  const readiness = mapElements
    .filter((element) => typeof element.pdfExportReady === "function")
    .map((element) => Promise.resolve().then(() => element.pdfExportReady()))

  if (readiness.length === 0) return { status: "ready", errors: [] }

  let timeoutId
  const settled = Promise.allSettled(readiness).then((results) => {
    const errors = results.filter((result) => result.status === "rejected").map((result) => result.reason)
    return { status: errors.length > 0 ? "failed" : "ready", errors }
  })
  const timeout = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve({ status: "timeout", errors: [] }), timeoutMs)
  })

  const result = await Promise.race([settled, timeout])
  clearTimeout(timeoutId)
  return result
}

let lightThemeHolds = 0

// The download is a document, so it stays light in dark mode. This changes the live page, not
// html2canvas's clone, because html2canvas copies computed styles from the live page for SVGs
// and anything inside a custom element such as <turbo-frame>. Holds are counted because each
// drawer's export runs on its own, and gb-pdf-capture stops transitions so nothing is captured
// mid-change. Returns a function that releases this hold.
export function holdLightThemeForCapture(body) {
  if (lightThemeHolds === 0 && !body.classList.contains("gb-auto-dark")) return () => {}

  if (lightThemeHolds === 0) {
    body.classList.add("gb-pdf-capture")
    body.classList.remove("gb-auto-dark")
  }
  lightThemeHolds += 1

  return () => {
    lightThemeHolds -= 1
    if (lightThemeHolds > 0) return

    body.classList.add("gb-auto-dark")
    body.classList.remove("gb-pdf-capture")
  }
}
